import React, { useMemo, useState, useEffect } from "react";
import Swal from "sweetalert2";
import { useAppSelector } from "../../../hooks/hooks";
import { fetchAxiosToken } from "../../../helpers/fetchAxiosToken";
import { NavbarHotel } from "../../dashboardHotel/NavbarHotel";
import { NavbarMobileHotel } from "../../dashboardHotel/NavbarMobileHotel";
import { NavbarBroker } from "pages/dashboardBroker/NavbarBroker";
import { NavbarMobileBroker } from "pages/dashboardBroker/NavbarMobileBroker";

type Form = {
    is_facturacion: string;
    clave_facturacion: string;
    mail_facturacion: string;
    ruc_facturacion: string; // usamos string para validar/mostrar sin parseos
    correo_facturacion: string;
    nombre_comercial: string;
    nombre_legal: string;
    numero_documento_identidad: string;
    tipo_documento_identidad: "RUC" | "DNI" | "CE" | "PASAPORTE";
};

const INITIAL: Form = {
    is_facturacion: "No",
    clave_facturacion: "",
    mail_facturacion: "",
    ruc_facturacion: "",
    correo_facturacion: "",
    nombre_comercial: "",
    nombre_legal: "",
    numero_documento_identidad: "",
    tipo_documento_identidad: "RUC",
};

const CONTROL =
    "w-full h-10 px-3 rounded-lg border border-[#E9E9E9] bg-white outline-none text-sm " +
    "focus:ring-2 focus:ring-[#1C4CBB]/40 focus:border-[#1C4CBB]";

export const DatosFacturacionHotel: React.FC = () => {
    const { id: id_usuario, tipo_usuario } = useAppSelector(
        (s) => s.usuario_logeado,
    );

    const [form, setForm] = useState<Form>(INITIAL);
    const [saving, setSaving] = useState(false);
    const [showClave, setShowClave] = useState(false);

    const endpoint = useMemo(
        () => `/usuarios/updateDatosFacturacion/${id_usuario}`,
        [id_usuario],
    );

    useEffect(() => {
        (async () => {
            try {
                const data = await fetchAxiosToken({
                    url: `/usuarios/getDatosFacturacion/${id_usuario}`,
                });

                if (data) {
                    setForm({
                        is_facturacion: String(
                            data.datos_facturacion.is_facturacion ?? "No",
                        ),
                        clave_facturacion:
                            data.datos_facturacion.clave_facturacion,
                        mail_facturacion:
                            data.datos_facturacion.mail_facturacion ?? "",
                        ruc_facturacion: String(
                            data.datos_facturacion.ruc_facturacion ?? "",
                        ),
                        correo_facturacion:
                            data.datos_facturacion.correo_facturacion ?? "",
                        nombre_comercial:
                            data.datos_facturacion.nombre_comercial ?? "",
                        nombre_legal: data.datos_facturacion.nombre_legal ?? "",
                        numero_documento_identidad: String(
                            data.datos_facturacion.numero_documento_identidad ??
                                "",
                        ),
                        tipo_documento_identidad:
                            (data.datos_facturacion
                                .tipo_documento_identidad as Form["tipo_documento_identidad"]) ??
                            "RUC",
                    });
                }
            } catch {}
        })();
    }, [id_usuario]);

    const onChange = <K extends keyof Form>(k: K, v: Form[K]) =>
        setForm((p) => ({ ...p, [k]: v }));

    const isEmail = (v: string) => /^\S+@\S+\.\S+$/.test(v);
    const isOnlyDigits = (v: string) => /^[0-9]+$/.test(v);

    const validate = () => {
        if (!(form.clave_facturacion ?? "").trim())
            return "La clave de facturación es requerida.";

        if (
            !(form.mail_facturacion ?? "").trim() ||
            !isEmail(form.mail_facturacion ?? "")
        )
            return "Email de integración (mail_facturacion) inválido.";
        if (
            !form.ruc_facturacion.trim() ||
            !isOnlyDigits(form.ruc_facturacion) ||
            form.ruc_facturacion.length !== 11
        )
            return "RUC de integración inválido (11 dígitos).";
        if (
            !form.correo_facturacion.trim() ||
            !isEmail(form.correo_facturacion)
        )
            return "Correo del emisor inválido.";
        if (!form.nombre_comercial.trim())
            return "El nombre comercial es requerido.";
        if (!form.nombre_legal.trim()) return "El nombre legal es requerido.";
        if (
            !form.numero_documento_identidad.trim() ||
            !isOnlyDigits(form.numero_documento_identidad) ||
            (form.tipo_documento_identidad === "RUC" &&
                form.numero_documento_identidad.length !== 11)
        )
            return "Número de documento del emisor inválido.";
        return null;
    };

    const handleSave = async () => {
        const err = validate();
        if (err) {
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Dato inválido",
                text: err,
                icon: "error",
            });
            return;
        }

        try {
            setSaving(true);
            // Body según especificación
            const body = {
                is_facturacion: form.is_facturacion,
                clave_facturacion: form.clave_facturacion,
                mail_facturacion: form.mail_facturacion,
                ruc_facturacion: Number(form.ruc_facturacion),
                correo_facturacion: form.correo_facturacion,
                nombre_comercial: form.nombre_comercial,
                nombre_legal: form.nombre_legal,
                numero_documento_identidad: form.numero_documento_identidad,
                tipo_documento_identidad: form.tipo_documento_identidad,
            };

            await fetchAxiosToken({
                url: endpoint,
                method: "put",
                body,
            });

            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Datos guardados",
                text: "La conexión de facturación se actualizó correctamente.",
                icon: "success",
            });
        } catch (e: any) {
            const msg =
                e?.response?.data?.message ??
                "No se pudo guardar la configuración.";
            Swal.fire({
                confirmButtonColor: "#029BFC",
                iconColor: "#029BFC",
                title: "Error",
                text: String(msg),
                icon: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const Page = (
        <div className="flex">
            <div className="w-full mt-6">
                <div className="grid w-11/12 max-w-6xl gap-6 mx-auto lg:grid-cols-2">
                    {/* Intro */}
                    <div className="text-[#4D4D4D]">
                        <p className="text-xl font-bold 2xl:text-2xl">
                            Facturación electrónica
                        </p>
                        <p className="mt-4 text-sm 2xl:text-base">
                            Conecta tu hotel con el proveedor de facturación.
                            Los campos con * son obligatorios.
                        </p>
                        <div className="p-4 mt-4 border rounded-lg bg-[#FAFAFA] text-sm">
                            <p className="font-medium">¿Qué necesitas?</p>
                            <ul className="pl-5 mt-2 list-disc">
                                <li>
                                    Clave de integración proporcionada por el
                                    proveedor.
                                </li>
                                <li>
                                    Correo de integración y RUC de integración.
                                </li>
                                <li>
                                    Datos del emisor (razón social / RUC /
                                    correo).
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Formulario */}
                    <div className="w-full p-4 border border-[#CCCCCC] rounded-lg shadow-lg">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-semibold text-[#4D4D4D]">
                                Configurar datos de facturación
                            </h3>

                            {/* TOGGLE NUEVO */}
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={form.is_facturacion === "Si"}
                                    onChange={(e) =>
                                        onChange(
                                            "is_facturacion",
                                            e.target.checked ? "Si" : "No",
                                        )
                                    }
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#029BFC]"></div>
                                <span className="ml-3 text-sm font-medium text-gray-700">
                                    {form.is_facturacion === "No"
                                        ? "Inactivo"
                                        : "Activo"}
                                </span>
                            </label>
                        </div>
                        <fieldset
                            disabled={form.is_facturacion === "No"}
                            className="group"
                        >
                            <div className="group-disabled:opacity-50 group-disabled:grayscale-[50%] transition-all duration-300">
                                {/* Bloque: Integración */}
                                <div className="p-3 mb-4 rounded-lg bg-[#F8FAFF] border border-[#E6ECFF]">
                                    <p className="mb-3 text-sm font-medium text-[#1C4CBB]">
                                        Datos de integración
                                    </p>

                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        <div className="flex flex-col gap-1">
                                            <label className="text-sm font-medium text-[#3C3C3C]">
                                                Clave de facturación *
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type={
                                                        showClave
                                                            ? "text"
                                                            : "password"
                                                    }
                                                    className={`${CONTROL} pr-10`}
                                                    value={
                                                        form?.clave_facturacion
                                                    }
                                                    onChange={(e) =>
                                                        onChange(
                                                            "clave_facturacion",
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="••••••••••"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setShowClave((s) => !s)
                                                    }
                                                    className="absolute inset-y-0 right-2 text-xs text-[#1C4CBB]"
                                                >
                                                    {showClave
                                                        ? "Ocultar"
                                                        : "Mostrar"}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label className="text-sm font-medium text-[#3C3C3C]">
                                                Email de integración *
                                            </label>
                                            <input
                                                type="email"
                                                className={CONTROL}
                                                value={form?.mail_facturacion}
                                                onChange={(e) =>
                                                    onChange(
                                                        "mail_facturacion",
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="integracion@proveedor.com"
                                                autoComplete="email"
                                            />
                                        </div>

                                        <div className="flex flex-col gap-1">
                                            <label className="text-sm font-medium text-[#3C3C3C]">
                                                RUC de integración *
                                            </label>
                                            <input
                                                className={CONTROL}
                                                value={form?.ruc_facturacion}
                                                onChange={(e) =>
                                                    onChange(
                                                        "ruc_facturacion",
                                                        e.target.value
                                                            .replace(
                                                                /[^0-9]/g,
                                                                "",
                                                            )
                                                            .slice(0, 11),
                                                    )
                                                }
                                                placeholder="2061********"
                                                inputMode="numeric"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Bloque: Emisor */}
                                <div className="p-3 mb-4 rounded-lg bg-[#FAFAFA] border border-[#E9E9E9]">
                                    <p className="mb-3 text-sm font-medium text-[#4D4D4D]">
                                        Datos del emisor
                                    </p>
                                    <div className="grid grid-cols-1 gap-3">
                                        <div className="flex flex-col gap-1">
                                            <label className="text-sm font-medium text-[#3C3C3C]">
                                                Correo emisor *
                                            </label>
                                            <input
                                                type="email"
                                                className={CONTROL}
                                                value={form?.correo_facturacion}
                                                onChange={(e) =>
                                                    onChange(
                                                        "correo_facturacion",
                                                        e.target.value,
                                                    )
                                                }
                                                placeholder="facturas@hotel.com"
                                                autoComplete="email"
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <div className="flex flex-col gap-1">
                                                <label className="text-sm font-medium text-[#3C3C3C]">
                                                    Nombre comercial *
                                                </label>
                                                <input
                                                    className={CONTROL}
                                                    value={
                                                        form?.nombre_comercial
                                                    }
                                                    onChange={(e) =>
                                                        onChange(
                                                            "nombre_comercial",
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="Mi Hotel"
                                                />
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <label className="text-sm font-medium text-[#3C3C3C]">
                                                    Nombre legal (Razón social)
                                                    *
                                                </label>
                                                <input
                                                    className={CONTROL}
                                                    value={form?.nombre_legal}
                                                    onChange={(e) =>
                                                        onChange(
                                                            "nombre_legal",
                                                            e.target.value,
                                                        )
                                                    }
                                                    placeholder="Mi Hotel S.A.C."
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                            <div className="flex flex-col gap-1">
                                                <label className="text-sm font-medium text-[#3C3C3C]">
                                                    Tipo documento *
                                                </label>
                                                <select
                                                    className={CONTROL}
                                                    value={
                                                        form?.tipo_documento_identidad
                                                    }
                                                    onChange={(e) =>
                                                        onChange(
                                                            "tipo_documento_identidad",
                                                            e.target
                                                                .value as Form["tipo_documento_identidad"],
                                                        )
                                                    }
                                                >
                                                    {/* Si tu proveedor solo acepta RUC, puedes dejarlo fijo/disabled */}
                                                    <option value="RUC">
                                                        RUC
                                                    </option>
                                                    <option value="DNI">
                                                        DNI
                                                    </option>
                                                    <option value="CE">
                                                        CE
                                                    </option>
                                                    <option value="PASAPORTE">
                                                        PASAPORTE
                                                    </option>
                                                </select>
                                            </div>

                                            <div className="flex flex-col gap-1">
                                                <label className="text-sm font-medium text-[#3C3C3C]">
                                                    N° documento emisor *
                                                </label>
                                                <input
                                                    className={CONTROL}
                                                    value={
                                                        form?.numero_documento_identidad
                                                    }
                                                    onChange={(e) =>
                                                        onChange(
                                                            "numero_documento_identidad",
                                                            e.target.value
                                                                .replace(
                                                                    /[^0-9]/g,
                                                                    "",
                                                                )
                                                                .slice(0, 15),
                                                        )
                                                    }
                                                    placeholder={
                                                        form?.tipo_documento_identidad ===
                                                        "RUC"
                                                            ? "2061********"
                                                            : "########"
                                                    }
                                                    inputMode="numeric"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </fieldset>

                        <div className="flex justify-center">
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="h-11 px-6 rounded-lg text-white bg-[#029BFC] hover:opacity-90 disabled:opacity-60"
                            >
                                {saving
                                    ? "Guardando..."
                                    : "Guardar configuración"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Exclusivo para HOTEL (tipo_usuario === 8)
    if (tipo_usuario !== 8 && tipo_usuario !== 6) return null;

    const EsBroker = tipo_usuario === 6;

    return (
        <>
            {EsBroker ? (
                <NavbarBroker titulo="Facturación electrónica">
                    {Page}
                    <NavbarMobileBroker />
                </NavbarBroker>
            ) : (
                <NavbarHotel titulo="Facturación electrónica">
                    {Page}
                    <NavbarMobileHotel />
                </NavbarHotel>
            )}
        </>
    );
};

export default DatosFacturacionHotel;
